import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Sunrise, Calendar, DollarSign, AlertCircle, Cloud, Sparkles, ArrowRight, Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import RescheduleConfirmDialog from "@/components/RescheduleConfirmDialog";

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function Chip({ label, value, testid }) {
  return (
    <div className="flex items-baseline gap-2" data-testid={testid}>
      <span className="text-3xl font-bold text-zinc-900 font-heading">{value}</span>
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono-accent">{label}</span>
    </div>
  );
}

export default function BusinessPulseCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/business-pulse");
      setData(data);
    } catch (e) {
      // Silent fail — Business Pulse is aspirational, not blocking.
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return (
    <div className="rounded-2xl bg-gradient-to-br from-orange-50 via-white to-emerald-50 border border-orange-100 p-6 mb-8" data-testid="business-pulse-loading">
      <Loader2 className="w-5 h-5 animate-spin text-[#FF5A00]"/>
    </div>
  );
  if (!data) return null;

  const s = data.schedule || {};
  const r = data.revenue || {};
  const w = data.weather;
  const rec = data.recommendation;
  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-orange-50 via-white to-emerald-50 border border-orange-100 p-6 mb-8 shadow-sm"
      data-testid="business-pulse-card"
    >
      <div className="flex items-center gap-2 mb-1">
        <Sunrise className="w-5 h-5 text-[#FF5A00]"/>
        <div className="text-[10px] uppercase tracking-widest text-[#FF5A00] font-mono-accent">Business pulse</div>
      </div>
      <h1 className="font-heading text-3xl font-bold" data-testid="pulse-greeting">{data.greeting}.</h1>
      <div className="text-sm text-zinc-500 mt-0.5">{data.date} · {data.business_name}</div>

      <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="pulse-schedule">
        <Chip label="Jobs today" value={s.jobs_today || 0} testid="pulse-jobs-today"/>
        <Chip label="Estimates" value={s.estimates_pending || 0} testid="pulse-estimates-pending"/>
        <Chip label="Invoices outstanding" value={s.invoices_outstanding || 0} testid="pulse-invoices-outstanding"/>
        <Chip label="Overnight payments" value={s.payments_overnight || 0} testid="pulse-payments-overnight"/>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4">
        <div className="rounded-lg bg-white border border-zinc-200 p-4" data-testid="pulse-revenue-week">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-emerald-600"/>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Revenue this week</div>
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-1 font-heading">{money(r.this_week)}</div>
        </div>
        <div className="rounded-lg bg-white border border-zinc-200 p-4" data-testid="pulse-outstanding">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600"/>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Outstanding</div>
          </div>
          <div className="text-2xl font-bold text-amber-700 mt-1 font-heading">{money(r.outstanding)}</div>
        </div>
      </div>

      {w && (
        <div className="mt-5 rounded-lg bg-white border border-sky-200 p-4" data-testid="pulse-weather">
          <div className="flex items-start gap-3">
            <Cloud className="w-6 h-6 text-sky-600 flex-shrink-0 mt-0.5"/>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">Weather · {w.condition} · {w.temperature_f}°F</div>
              <div className="text-sm text-zinc-600">{w.recommendation}</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {w.today && (
                  <div className="rounded border border-zinc-200 p-2" data-testid="pulse-weather-today">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Today</div>
                    <div className="text-sm font-semibold mt-0.5">{w.today.high_f}° / {w.today.low_f}°</div>
                    <div className="text-xs text-zinc-500">💧 {w.today.rain_prob_pct}% · {w.today.rain_inches}" · 💨 {w.today.wind_mph_max} mph</div>
                  </div>
                )}
                {w.tomorrow && (
                  <div className="rounded border border-zinc-200 p-2" data-testid="pulse-weather-tomorrow">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-accent">Tomorrow · {w.tomorrow.condition}</div>
                    <div className="text-sm font-semibold mt-0.5">{w.tomorrow.high_f}° / {w.tomorrow.low_f}°</div>
                    <div className="text-xs text-zinc-500">💧 {w.tomorrow.rain_prob_pct}% · {w.tomorrow.rain_inches}" · 💨 {w.tomorrow.wind_mph_max} mph</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {data.weather_risk?.message && (
            <div
              className={`mt-3 rounded-md p-3 text-sm ${
                data.weather_risk.severity === "high"
                  ? "bg-rose-50 border border-rose-200 text-rose-800"
                  : "bg-amber-50 border border-amber-200 text-amber-800"
              }`}
              data-testid="pulse-weather-risk"
            >
              <span className="font-semibold mr-1">
                {data.weather_risk.severity === "high" ? "Reschedule recommended:" : "Heads up:"}
              </span>
              {data.weather_risk.message}
            </div>
          )}
        </div>
      )}

      {rec && (
        <div className="mt-5 rounded-lg bg-[#FF5A00] text-white p-5 flex items-start gap-3" data-testid="pulse-recommendation">
          <Sparkles className="w-5 h-5 flex-shrink-0 mt-0.5"/>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest opacity-80 font-mono-accent">One recommendation</div>
            <div className="font-bold text-lg mt-0.5">{rec.title}</div>
            <div className="text-sm opacity-90 mt-0.5">{rec.reason}</div>
          </div>
          {rec.action_url && (
            <Link
              to={rec.action_url}
              className="inline-flex items-center gap-1 bg-white/20 hover:bg-white/30 text-white text-sm px-3 py-1.5 rounded-lg"
              data-testid="pulse-recommendation-cta"
            >
              Open <ArrowRight className="w-3 h-3"/>
            </Link>
          )}
        </div>
      )}

      {/* Reschedule proposals (only surfaces when weather-risk is high) */}
      <RescheduleProposals
        show={data.weather_risk?.severity === "high"}
        businessName={data.business_name}
        onDone={load}
      />
    </div>
  );
}

function RescheduleProposals({ show, businessName, onDone }) {
  const [loading, setLoading] = useState(false);
  const [proposals, setProposals] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // {proposal, candidate}

  useEffect(() => {
    if (!show) { setProposals(null); return; }
    setLoading(true);
    api.post("/business-pulse/reschedule-proposals", { days_out: 7 })
      .then((r) => setProposals(r.data.proposals || []))
      .catch(() => setProposals([]))
      .finally(() => setLoading(false));
  }, [show]);

  const onSuccess = (data) => {
    if (!data?.ok) return;
    // Remove from proposals list to prevent duplicate clicks
    setProposals((cur) => (cur || []).filter((p) => p.job_id !== confirmTarget?.proposal?.job_id));
    // Refresh pulse (and any calendar upstream via parent)
    onDone && onDone();
  };

  if (!show) return null;
  if (loading) return <div className="mt-5 rounded-lg bg-white border border-zinc-200 p-4 text-sm text-zinc-500">Finding replacement slots…</div>;
  if (!proposals || proposals.length === 0) return null;
  return (
    <div className="mt-5 rounded-lg bg-white border border-amber-200 p-4" data-testid="pulse-reschedule-proposals">
      <div className="font-heading font-bold flex items-center gap-2 mb-2">
        Reschedule proposals
      </div>
      <div className="text-xs text-zinc-500 mb-3">Weather risk is high tomorrow. Pick a replacement slot to review, confirm, and notify the customer. Nothing changes until you click <span className="font-semibold">Confirm reschedule</span>.</div>
      <div className="space-y-3">
        {proposals.map((p) => (
          <div key={p.job_id} className="border border-zinc-200 rounded p-3" data-testid={`pulse-proposal-${p.job_id}`}>
            <div className="text-sm font-semibold">{p.job_title}</div>
            <div className="text-[11px] text-zinc-500">
              {p.customer_name ? <>{p.customer_name} · </> : null}
              Currently: {p.current_slot?.slice(0,16).replace('T',' ')}
            </div>
            <div className="mt-2 space-y-2">
              {p.candidates.length === 0 ? (
                <div className="text-xs text-zinc-400 italic">No open slots this week — reach out to the customer directly.</div>
              ) : p.candidates.map((c, i) => (
                <div key={c.slot} className="flex items-center justify-between gap-3 text-sm" data-testid={`pulse-candidate-${p.job_id}-${i}`}>
                  <div>
                    <div className="font-medium">{c.slot?.slice(0,16).replace('T',' ')}</div>
                    <div className="text-[11px] text-zinc-500">{c.reason} · score {c.score}</div>
                  </div>
                  <button
                    onClick={() => setConfirmTarget({ proposal: p, candidate: c })}
                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded"
                    data-testid={`pulse-approve-${p.job_id}-${i}`}
                  >
                    Move to this slot
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <RescheduleConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
        proposal={confirmTarget?.proposal}
        candidate={confirmTarget?.candidate}
        businessName={businessName}
        onSuccess={onSuccess}
      />
    </div>
  );
}
